import { gql, useQuery } from "@apollo/client";
import { WBButton } from "@apptractive/design-system-web";
import { Box } from "@mui/material";
import {
  LOCATION_NEW_SOUTH_WALES,
  LOCATION_NEW_SOUTH_WALES_ACTIVE,
  LOCATION_NORTHER_TERRITORY,
  LOCATION_NORTHER_TERRITORY_ACTIVE,
  LOCATION_QUEENSLAND,
  LOCATION_QUEENSLAND_ACTIVE,
  LOCATION_SOUTH_AUSTRALIA,
  LOCATION_SOUTH_AUSTRALIA_ACTIVE,
  LOCATION_TASMANIA,
  LOCATION_TASMANIA_ACTIVE,
  LOCATION_VICTORIA,
  LOCATION_VICTORIA_ACTIVE,
  LOCATION_WESTERN,
  LOCATION_WESTERN_ACTIVE,
} from "@apptractive/stm-shared";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Option } from "../API";
import { optionsByGroup } from "../graphql/queries";
import { SelectOption } from "./SelectOptionsForm";

interface AustraliaMapProps {
  selectedOptions: Option[];
  onSubmit: Function;
  isLoading: boolean;
}

export const AustraliaMap = ({
  selectedOptions,
  onSubmit,
  isLoading,
}: AustraliaMapProps) => {
  const { t } = useTranslation();
  const [interests, setInterests] = useState<SelectOption[]>([]);

  const { data: optionsData, loading: optionsLoading } = useQuery(
    gql(optionsByGroup),
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        group: `locations-AU`,
      },
    }
  );

  const options = useMemo(
    () => optionsData?.optionsByGroup?.items || [],
    [optionsData]
  );

  useEffect(() => {
    if (!isEmpty(options)) {
      const selOptions: any = [...options].map((tag: Option) => ({
        ...tag,
        isActive:
          selectedOptions?.find(
            (selectedOption) => selectedOption.value === tag.value
          ) || false,
      }));

      setInterests(selOptions);
    }
  }, [selectedOptions, options]);

  const onInterestClick = (clickedInterest?: SelectOption) => {
    if (clickedInterest) {
      const newInterests: SelectOption[] = [...interests];
      const index = newInterests.findIndex(
        (interest: SelectOption) => interest.value === clickedInterest.value
      );

      newInterests[index] = {
        ...newInterests[index],
        isActive: !clickedInterest.isActive,
      };

      setInterests(newInterests);
    }
  };

  const onSave = async () => {
    const selectedInterests = [...interests]
      .filter((interest) => interest.isActive)
      .map((interest) => {
        const { isActive, ...other } = interest;
        return other;
      });

    onSubmit(selectedInterests);
  };

  return (
    <>
      <Box
        sx={{
          width: "365px",
          height: "380px",
          position: "relative",
          textAlign: "left",
          margin: "20px auto",
        }}
      >
        <img
          alt="Western Australia"
          src={
            interests.find(
              (interest) => interest.value === "WA" && interest.isActive
            )
              ? LOCATION_WESTERN_ACTIVE.default
              : LOCATION_WESTERN.default
          }
          style={{
            position: "absolute",
            width: "145.2px",
            height: "233.8px",
            top: "31px",
          }}
          onClick={() =>
            onInterestClick(
              interests.find((option: SelectOption) => option.value === "WA")
            )
          }
        />
        <img
          alt="Nothern Territory"
          src={
            interests.find(
              (interest) => interest.value === "NT" && interest.isActive
            )
              ? LOCATION_NORTHER_TERRITORY_ACTIVE.default
              : LOCATION_NORTHER_TERRITORY.default
          }
          style={{
            position: "absolute",
            width: "91.9px",
            height: "159.3px",
            left: "137.5px",
            top: "1px",
          }}
          onClick={() =>
            onInterestClick(
              interests.find((option: SelectOption) => option.value === "NT")
            )
          }
        />
        <img
          alt="Queensland"
          src={
            interests.find(
              (interest) => interest.value === "QLD" && interest.isActive
            )
              ? LOCATION_QUEENSLAND_ACTIVE.default
              : LOCATION_QUEENSLAND.default
          }
          style={{
            position: "absolute",
            width: "140px",
            height: "203px",
            left: "223px",
          }}
          onClick={() =>
            onInterestClick(
              interests.find((option: SelectOption) => option.value === "QLD")
            )
          }
        />
        <img
          alt="South Australia"
          src={
            interests.find(
              (interest) => interest.value === "SA" && interest.isActive
            )
              ? LOCATION_SOUTH_AUSTRALIA_ACTIVE.default
              : LOCATION_SOUTH_AUSTRALIA.default
          }
          style={{
            position: "absolute",
            width: "110px",
            height: "130px",
            left: "141px",
            top: " 156px",
          }}
          onClick={() =>
            onInterestClick(
              interests.find((option: SelectOption) => option.value === "SA")
            )
          }
        />
        <img
          alt="New South Wales"
          src={
            interests.find(
              (interest) => interest.value === "NSW" && interest.isActive
            )
              ? LOCATION_NEW_SOUTH_WALES_ACTIVE.default
              : LOCATION_NEW_SOUTH_WALES.default
          }
          style={{
            position: "absolute",
            width: "120px",
            height: "95px",
            left: "243.5px",
            top: "188px",
          }}
          onClick={() =>
            onInterestClick(
              interests.find((option: SelectOption) => option.value === "NSW")
            )
          }
        />
        <img
          alt="Tasmania"
          src={
            interests.find(
              (interest) => interest.value === "TAS" && interest.isActive
            )
              ? LOCATION_TASMANIA_ACTIVE.default
              : LOCATION_TASMANIA.default
          }
          style={{
            position: "absolute",
            width: "100px",
            height: "40px",
            left: "199px",
            top: "315px",
          }}
          onClick={() =>
            onInterestClick(
              interests.find((option: SelectOption) => option.value === "TAS")
            )
          }
        />
        <img
          alt="Victoria"
          src={
            interests.find(
              (interest) => interest.value === "VIC" && interest.isActive
            )
              ? LOCATION_VICTORIA_ACTIVE.default
              : LOCATION_VICTORIA.default
          }
          style={{
            position: "absolute",
            width: "77px",
            height: "59px",
            left: "241px",
            top: "240px",
          }}
          onClick={() =>
            onInterestClick(
              interests.find((option: SelectOption) => option.value === "VIC")
            )
          }
        />
      </Box>
      <WBButton
        onClick={onSave}
        disabled={optionsLoading || isLoading}
        sx={{
          mt: 3,
          alignSelf: "center",
        }}
      >
        {t("saveTitle", { ns: "common" })}
      </WBButton>
    </>
  );
};
